import { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  LinearProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import LockIcon from "@mui/icons-material/Lock";

const signs = [
  "Aries",
  "Taurus",
  "Gemini",
  "Cancer",
  "Leo",
  "Virgo",
  "Libra",
  "Scorpio",
  "Sagittarius",
  "Capricorn",
  "Aquarius",
  "Pisces",
];

const areas = [
  { label: "Love & romance", score: 82, color: "#D4537E" },
  { label: "Communication", score: 70, color: "#534AB7" },
  { label: "Trust", score: 85, color: "#639922" },
  { label: "Long-term", score: 75, color: "#EF9F27" },
];

export default function Compatibility() {
  const [p1, setP1] = useState({ name: "", dob: "", sign: "" });
  const [p2, setP2] = useState({ name: "", dob: "", sign: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = () => {
    if (!p1.sign || !p2.sign) return;
    setLoading(true);
    setTimeout(() => {
      const scores = [68, 72, 75, 78, 80, 82, 85, 88, 90];
      setResult(scores[Math.floor(Math.random() * scores.length)]);
      setLoading(false);
    }, 1800);
  };

  return (
  <Container maxWidth="md" sx={{ py: 4 }}>
    
    {/* Title - centered */}
    <Box sx={{ textAlign: 'center', mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center',
        justifyContent: 'center', gap: 1, mb: 0.5 }}>
        <FavoriteIcon sx={{ color: '#D4537E' }} />
        <Typography variant="h6" fontWeight={500}
          sx={{ color: '#E8E6FF' }}>
          Love compatibility checker
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: '#8B85B8' }}>
        Enter both birth details to see your cosmic compatibility score
      </Typography>
    </Box>

    {/* Cards + button centered */}
<Box sx={{ 
  display: 'flex', 
  flexDirection: 'column', 
  alignItems: 'center',
  width: '100%'
}}>

  {/* Two cards side by side - centered */}
  <Box sx={{ 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    gap: 2,
    mb: 3,
    width: '100%'
  }}>
    
    {/* Your details card */}
    <Card sx={{ width: 380, flexShrink: 0 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="caption"
          sx={{ color: '#8B85B8', fontWeight: 500,
            display: 'block', mb: 1.5 }}>
          Your details
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField size="small" fullWidth label="Name"
            value={p1.name}
            onChange={e => setP1({ ...p1, name: e.target.value })} />
          <TextField size="small" fullWidth
            label="Date of birth" type="date"
            InputLabelProps={{ shrink: true }}
            value={p1.dob}
            onChange={e => setP1({ ...p1, dob: e.target.value })}
            sx={{
              '& .MuiInputLabel-root': {
                transform: 'translate(14px, -9px) scale(0.75)',
                background: '#120F20', px: 0.5, fontSize: 11,
              },
              '& input': { paddingTop: '14px', paddingBottom: '6px', fontSize: 12 },
            }} />
          <FormControl fullWidth size="small">
            <InputLabel>Your sign</InputLabel>
            <Select value={p1.sign} label="Your sign"
              onChange={e => setP1({ ...p1, sign: e.target.value })}>
              {signs.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
      </CardContent>
    </Card>

    {/* Heart in middle */}
    <Box sx={{
      width: 44, height: 44, borderRadius: '50%',
      bgcolor: '#1A1225', border: '1px solid #72243E',
      display: 'flex', alignItems: 'center',
      justifyContent: 'center', flexShrink: 0,
    }}>
      <FavoriteIcon sx={{ color: '#D4537E', fontSize: 22 }} />
    </Box>

    {/* Partner's details card */}
    <Card sx={{ width: 380, flexShrink: 0 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="caption"
          sx={{ color: '#8B85B8', fontWeight: 500,
            display: 'block', mb: 1.5 }}>
          Partner's details
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField size="small" fullWidth label="Name"
            value={p2.name}
            onChange={e => setP2({ ...p2, name: e.target.value })} />
          <TextField size="small" fullWidth
            label="Date of birth" type="date"
            InputLabelProps={{ shrink: true }}
            value={p2.dob}
            onChange={e => setP2({ ...p2, dob: e.target.value })}
            sx={{
              '& .MuiInputLabel-root': {
                transform: 'translate(14px, -9px) scale(0.75)',
                background: '#120F20', px: 0.5, fontSize: 11,
              },
              '& input': { paddingTop: '14px', paddingBottom: '6px', fontSize: 12 },
            }} />
          <FormControl fullWidth size="small">
            <InputLabel>Their sign</InputLabel>
            <Select value={p2.sign} label="Their sign"
              onChange={e => setP2({ ...p2, sign: e.target.value })}>
              {signs.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
      </CardContent>
    </Card>

  </Box>

  {/* Button centered below cards */}
  <Button variant="contained" size="large"
    onClick={check} disabled={loading}
    startIcon={<FavoriteIcon />}
    sx={{
      borderRadius: 5, py: 1.2, px: 5,
      bgcolor: '#D4537E', fontSize: 14, mb: 2,
      '&:hover': { bgcolor: '#993556' },
    }}>
    {loading ? 'Checking...' : 'Check compatibility'}
  </Button>

  {loading && (
    <LinearProgress sx={{ mb: 2, width: '100%',
      '& .MuiLinearProgress-bar': { bgcolor: '#D4537E' } }} />
  )}

  {/* Result card */}
  {result && (
    <Card sx={{ width: '100%', maxWidth: 620 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', mb: 1 }}>
          <Box>
            <Typography fontWeight={500}
              sx={{ color: '#E8E6FF', fontSize: 14 }}>
              {p1.name || 'You'} ({p1.sign}) + {p2.name || 'Partner'} ({p2.sign})
            </Typography>
            <Typography variant="caption" sx={{ color: '#8B85B8' }}>
              Overall compatibility
            </Typography>
          </Box>
          <Typography fontWeight={600} fontSize={28}
            sx={{ color: '#8B7FFF' }}>
            {result}%
          </Typography>
        </Box>

        <LinearProgress variant="determinate" value={result}
          sx={{ height: 8, borderRadius: 4, mb: 3 }} />

        <Typography variant="body2"
          sx={{ color: '#8B85B8', lineHeight: 1.7, mb: 3 }}>
          {p1.sign} and {p2.sign} have a deeply emotional connection
          despite being very different. With patience this pairing can
          be beautifully complementary. The planets favour this union —
          especially after November 2025.
        </Typography>

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {areas.map(a => (
            <Grid item xs={6} key={a.label}>
              <Box sx={{ bgcolor: '#0D0B1A', borderRadius: 2,
                p: 1.5, border: '1px solid #1E1A3A' }}>
                <Typography variant="caption"
                  sx={{ color: '#8B85B8' }}>{a.label}</Typography>
                <Box sx={{ height: 4, bgcolor: '#1E1A3A',
                  borderRadius: 2, my: 0.8, overflow: 'hidden' }}>
                  <Box sx={{ height: 4, borderRadius: 2,
                    width: `${a.score}%`, bgcolor: a.color }} />
                </Box>
                <Typography variant="caption" fontWeight={500}
                  sx={{ color: '#A89EFF' }}>{a.score}%</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ bgcolor: '#0D0B1A', borderRadius: 2, p: 2,
          border: '1px solid #2A2550', textAlign: 'center' }}>
          <LockIcon sx={{ color: '#8B7FFF', fontSize: 16,
            verticalAlign: 'middle', mr: 0.5 }} />
          <Typography component="span" variant="caption"
            sx={{ color: '#8B85B8' }}>
            Unlock marriage timing + full remedies —{' '}
          </Typography>
          <Typography component="a" href="/pricing"
            variant="caption" fontWeight={500}
            sx={{ color: '#8B7FFF', cursor: 'pointer' }}>
            Upgrade for ₹99
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )}

</Box>
  </Container>
)
}
